/**
 * Export helpers for the Reports module.
 *
 * exportCsv : pure-JS CSV writer (RFC 4180 quoting), triggers a browser download.
 *             Exports only real data rows (synthetic group headers / subtotals / grand totals
 *             are skipped — totals are easy to recompute in Excel and group structure isn't
 *             expressible in CSV anyway).
 *
 * exportXlsx: uses `xlsx-js-style` (a drop-in fork of SheetJS Community Edition that adds
 *             cell-style writing support). Exports the *visible* grid layout — group header
 *             rows, data rows, subtotal rows, and the pinned grand total — in the same order
 *             they appear on screen, with bold + colored backgrounds on synthetic rows so the
 *             group structure reads at a glance in Excel.
 */

const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 200);
};

const csvCell = (value) => {
    if (value === null || value === undefined) return '';
    let s = String(value);
    // RFC 4180: if the value contains a comma, double-quote, or line break, wrap in quotes and escape internal quotes.
    if (/[",\r\n]/.test(s)) {
        s = '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
};

export const exportCsv = (columns, rows, filename) => {
    const lines = [];
    lines.push(columns.map(csvCell).join(','));
    for (const row of rows) {
        if (row && (row.__type__ === 'subtotal' || row.__type__ === 'grand_total' || row.__type__ === 'group_header')) continue;
        lines.push(columns.map(c => csvCell(row?.[c])).join(','));
    }
    // Prefix with BOM so Excel correctly detects UTF-8.
    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    triggerDownload(blob, `${filename || 'report'}.csv`);
};

// Numeric detection identical to the one in the result grid, kept local to avoid an extra import.
const isNumericLike = (v) => {
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'number') return Number.isFinite(v);
    if (typeof v === 'string') {
        const s = v.replace(',', '.').trim();
        if (s === '') return false;
        if (s.length > 1 && /^0\d/.test(s)) return false;
        return !isNaN(Number(s));
    }
    return false;
};

const toNumber = (v) => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
        const s = v.replace(',', '.').trim();
        const n = Number(s);
        return Number.isFinite(n) ? n : 0;
    }
    return 0;
};

const DATE_ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)(?:Z|[+-]\d{2}:?\d{2})?)?$/;
const formatDateIfIso = (value) => {
    if (typeof value !== 'string') return null;
    const m = DATE_ISO_RE.exec(value.trim());
    if (!m) return null;
    const [, y, mo, d, time] = m;
    if (y === '0000' || mo === '00' || d === '00') return '';
    return time ? `${d}/${mo}/${y} ${time}` : `${d}/${mo}/${y}`;
};

// Cell-style presets. xlsx-js-style consumes the `s` property and writes the corresponding
// `styles.xml` entries into the workbook. Colors picked to match the on-screen tints.
const STYLE_HEADER_TABLE = {
    font: { bold: true, color: { rgb: '1F2937' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'D1D5DB' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
        bottom: { style: 'thin', color: { rgb: '6B7280' } },
    },
};
const STYLE_GROUP_HEADER = {
    font: { bold: true, color: { rgb: '14532D' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'BBF7D0' } },
    alignment: { vertical: 'center' },
};
const STYLE_SUBTOTAL = {
    font: { bold: true, color: { rgb: '312E81' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'C7D2FE' } },
    alignment: { vertical: 'center' },
    border: {
        top: { style: 'thin', color: { rgb: '6366F1' } },
    },
};
const STYLE_GRAND_TOTAL = {
    font: { bold: true, color: { rgb: '7F1D1D' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FECACA' } },
    alignment: { vertical: 'center' },
    border: {
        top:    { style: 'medium', color: { rgb: 'B91C1C' } },
        bottom: { style: 'medium', color: { rgb: 'B91C1C' } },
    },
};
const STYLE_NUMERIC_RIGHT = { alignment: { horizontal: 'right' } };

const colLetter = (idx) => {
    let n = idx + 1;
    let s = '';
    while (n > 0) {
        const r = (n - 1) % 26;
        s = String.fromCharCode(65 + r) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
};

export const exportXlsx = async (columns, rows, filename, options = {}) => {
    const { numericColumns = new Set(), pinnedBottomRow = null } = options;
    try {
        // xlsx-js-style is a drop-in fork of SheetJS CE with cell-style writing enabled.
        // If it isn't installed (e.g. dependency not yet npm-installed) we transparently fall
        // back to the stock `xlsx` package — the layout still exports correctly, just without
        // the colored backgrounds.
        let XLSX;
        try {
            XLSX = await import('xlsx-js-style');
        } catch (e) {
            XLSX = await import('xlsx');
        }

        // Build the linear row stream: header → all visible rows (data + synthetic) → pinned grand total.
        const visibleRows = Array.isArray(rows) ? rows.slice() : [];
        if (pinnedBottomRow) visibleRows.push(pinnedBottomRow);

        // Write directly via cell objects so we can carry types + styles per cell.
        const ws = {};
        const numericColSet = numericColumns instanceof Set
            ? numericColumns
            : new Set(Array.isArray(numericColumns) ? numericColumns : []);

        // Row 0 = column headers.
        columns.forEach((header, ci) => {
            const ref = `${colLetter(ci)}1`;
            ws[ref] = { t: 's', v: String(header), s: STYLE_HEADER_TABLE };
        });

        // Subsequent rows.
        visibleRows.forEach((row, ri) => {
            const rIdx = ri + 2; // 1-based + header
            const type = row?.__type__;
            const isHeader   = type === 'group_header';
            const isSubtotal = type === 'subtotal';
            const isGrand    = type === 'grand_total';

            columns.forEach((col, ci) => {
                const ref = `${colLetter(ci)}${rIdx}`;
                const raw = row?.[col];

                let cell;
                if (raw === null || raw === undefined || raw === '') {
                    cell = { t: 's', v: '' };
                } else {
                    // Numeric column with a numeric value → write as a real number so Excel can sum/sort.
                    const isNumericHere = numericColSet.has(col) && isNumericLike(raw);
                    if (isNumericHere) {
                        cell = { t: 'n', v: toNumber(raw) };
                    } else {
                        const dateFormatted = formatDateIfIso(raw);
                        cell = { t: 's', v: dateFormatted !== null ? dateFormatted : String(raw) };
                    }
                }

                // Apply per-row styling. Synthetic rows get bold + tinted backgrounds.
                if (isHeader)        cell.s = STYLE_GROUP_HEADER;
                else if (isSubtotal) cell.s = STYLE_SUBTOTAL;
                else if (isGrand)    cell.s = STYLE_GRAND_TOTAL;
                else if (cell.t === 'n') cell.s = STYLE_NUMERIC_RIGHT;

                ws[ref] = cell;
            });
        });

        const lastRow = 1 + visibleRows.length; // 1 for header
        const lastCol = Math.max(0, columns.length - 1);
        ws['!ref'] = `A1:${colLetter(lastCol)}${lastRow}`;

        // Approximate column widths from header + first ~100 rows.
        ws['!cols'] = columns.map((header) => {
            let maxLen = String(header).length;
            const sampleLimit = Math.min(visibleRows.length, 100);
            for (let i = 0; i < sampleLimit; i += 1) {
                const v = visibleRows[i]?.[header];
                if (v === null || v === undefined) continue;
                const len = String(v).length;
                if (len > maxLen) maxLen = len;
            }
            return { wch: Math.min(60, Math.max(8, maxLen + 2)) };
        });

        // Freeze the header row so column titles stay visible while scrolling.
        ws['!freeze'] = { xSplit: 0, ySplit: 1 };

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, (filename || 'Report').slice(0, 31));

        const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        triggerDownload(blob, `${filename || 'report'}.xlsx`);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('XLSX export failed, falling back to CSV. Reason:', err);
        exportCsv(columns, rows, filename);
    }
};

/**
 * "XLSX - raw data": every column and every row of the report query result
 * exactly as the query returned it - no layout columns, no grouping, no
 * subtotals and no on-screen search filter (the query's own GROUP BY of
 * course still applies). Numeric columns are written as numbers.
 */
export const exportRawXlsx = (columns, rows, filename) => {
    const cols = Array.isArray(columns) ? columns : [];
    const data = Array.isArray(rows) ? rows : [];
    const numeric = new Set();
    cols.forEach((c) => {
        let seen = 0;
        let ok = true;
        for (let i = 0; i < data.length && seen < 200; i++) {
            const v = data[i] ? data[i][c] : null;
            if (v === null || v === undefined || v === '') continue;
            seen++;
            if (!isNumericLike(v)) { ok = false; break; }
        }
        if (ok && seen > 0) numeric.add(c);
    });
    return exportXlsx(cols, data, (filename || 'report') + '_raw', { numericColumns: numeric });
};
